// ─────────────────────────────────────────────────────────────────────────
// INCIDENTS PANEL  (System tab — superusers only, wishlist #18)
//
// The incident ledger: database restarts / outages, failed tasks, workers killed
// mid-task, a stopped monitor. One row per (kind, source) per hour with its count,
// whether it was emailed (and the send outcome), and the incident that caused it.
// Click a row for the redacted traceback and its consequences. Read-only.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useSortableRows, SortableTh } from '../../../components/tables/SortableTable';

const KIND_STYLE = {
  db_restarted: 'text-red-600 dark:text-red-400',
  db_unreachable: 'text-red-600 dark:text-red-400',
  worker_killed: 'text-red-600 dark:text-red-400',
  monitor_stopped: 'text-red-600 dark:text-red-400',
  task_failed: 'text-amber-600 dark:text-amber-400',
};

const PAGE_LABEL = {
  sent: 'emailed', failed: 'email failed', throttled: 'repeat', folded: 'in cause',
  pending: 'waiting', none: 'logged',
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const short = (src) => (src || '').split('.').pop();

const COLS = [
  { key: 'when', type: 'number', get: (r) => new Date(r.last_seen).getTime() },
  { key: 'kind', type: 'string', get: (r) => r.kind_label },
  { key: 'source', type: 'string', get: (r) => short(r.source) },
  { key: 'count', type: 'number', get: (r) => r.count },
  { key: 'page', type: 'string', get: (r) => r.page_state },
];

function Detail({ id, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    tradingService.getIncident(id).then((r) => setD(r.data)).catch((e) => setErr(e.message));
  }, [id]);
  return (
    <tr>
      <td colSpan={5} className="py-2 px-2 bg-gray-50 dark:bg-[#161b22]">
        {err && <div className="text-[11px] text-red-600 dark:text-red-400">Error: {err}</div>}
        {!d && !err && <div className="text-[11px] text-gray-500">Loading…</div>}
        {d && (
          <div className="space-y-2">
            <div className="text-[11px] text-gray-700 dark:text-gray-300 break-words">{d.message || '—'}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">
              First {fmt(d.first_seen)} · last {fmt(d.last_seen)} · {d.source}
              {d.page_result ? ` · email: ${d.page_result}` : ''}
              {d.peak_mb != null ? ` · peak ${d.peak_mb} MB` : ''}
              {d.cause_id ? ` · caused by #${d.cause_id}` : ''}
            </div>
            {d.consequences?.length > 0 && (
              <div className="text-[11px] text-gray-600 dark:text-gray-300">
                Caused: {d.consequences.map((c) => `${short(c.source)}${c.count > 1 ? ` ×${c.count}` : ''}`).join(', ')}
              </div>
            )}
            {d.traceback && (
              <pre className="text-[10px] leading-snug font-mono text-gray-600 dark:text-gray-400 max-h-64 overflow-auto scrollbar-thin whitespace-pre-wrap break-all">{d.traceback}</pre>
            )}
            <button onClick={onClose} className="text-[11px] text-brand-gold hover:underline">Close</button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function IncidentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tradingService.getIncidents(30);
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Superusers only.' : (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = useMemo(() => data?.incidents || [], [data]);
  const { sorted, toggle, indicator } = useSortableRows(rows, COLS);
  const paged = rows.filter((r) => r.severity === 'page').length;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <button onClick={() => setCollapsed((c) => !c)} className="text-left">
          <h3 className="text-sm font-serif text-gray-900 dark:text-gray-100">
            Incidents <span className="text-gray-400 text-xs">{collapsed ? '▸' : '▾'}</span>
          </h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Last 30 days · {rows.length} recorded · {paged} paging-level. Database restarts and outages,
            failed tasks, workers killed mid-task.
          </p>
        </button>
        <button
          onClick={fetchData}
          disabled={loading}
          className="text-[11px] px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold/50 disabled:opacity-50 shrink-0"
        >
          {loading ? '…' : '↻'} Refresh
        </button>
      </div>

      {error && <div className="text-[11px] text-red-600 dark:text-red-400 mb-2">{error}</div>}

      {!collapsed && !error && (rows.length === 0 ? (
        <div className="text-[11px] text-emerald-600 dark:text-emerald-400 py-2">✓ No incidents in the last 30 days.</div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                <SortableTh label="When" colKey="when" toggle={toggle} indicator={indicator} align="left" className="py-1.5 pr-3 font-medium" />
                <SortableTh label="Kind" colKey="kind" toggle={toggle} indicator={indicator} align="left" className="py-1.5 pr-3 font-medium" />
                <SortableTh label="Source" colKey="source" toggle={toggle} indicator={indicator} align="left" className="py-1.5 pr-3 font-medium" />
                <SortableTh label="×" colKey="count" toggle={toggle} indicator={indicator} className="py-1.5 pr-3 font-medium" />
                <SortableTh label="Email" colKey="page" toggle={toggle} indicator={indicator} align="left" className="py-1.5 pr-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    onClick={() => setOpen(open === r.id ? null : r.id)}
                    className="border-b border-gray-100 dark:border-gray-800/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#161b22]"
                  >
                    <td className="py-1.5 pr-3 font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmt(r.last_seen)}</td>
                    <td className={`py-1.5 pr-3 font-semibold whitespace-nowrap ${KIND_STYLE[r.kind] || ''}`}>{r.kind_label}</td>
                    <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300 max-w-[14rem] truncate" title={r.source}>
                      {short(r.source)}{r.cause_id ? <span className="text-gray-400"> ← #{r.cause_id}</span> : null}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{r.count}</td>
                    <td className={`py-1.5 pr-3 whitespace-nowrap ${r.page_state === 'failed' ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                      {PAGE_LABEL[r.page_state] || r.page_state}
                    </td>
                  </tr>
                  {open === r.id && <Detail id={r.id} onClose={() => setOpen(null)} />}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
